import React, { useEffect, useState } from "react";
import { api, mediaUrl } from "../lib/api";
import { Megaphone, X, Eye, Camera, MapPin, Send, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { userIdentity } from "../lib/identity";
import { AttachmentPicker } from "./AttachmentPicker";

const KEY_DISMISSED = "rr_bc_dismissed";

const distanceKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const readDismissed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(KEY_DISMISSED) || "[]")); }
  catch (_e) { return new Set(); }
};
const writeDismissed = (set) => {
  try { localStorage.setItem(KEY_DISMISSED, JSON.stringify([...set])); } catch (_e) { /* noop */ }
};

const colors = {
  info: "bg-sky-600 text-white border-sky-700",
  warning: "bg-amber-600 text-white border-amber-700",
  critical: "bg-rescue-red text-white border-rescue-red",
};

// Multiple active diffusions in the same zone are ranked by severity
// before recency (Critique > Avertissement > Info).
const SEV_RANK = { critical: 3, warning: 2, info: 1 };

export const BroadcastBanner = ({ coords }) => {
  const [bcs, setBcs] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed());
  const [openId, setOpenId] = useState(null);
  const [reportFor, setReportFor] = useState(null); // broadcast object when sighting modal is open

  useEffect(() => {
    let mounted = true;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/broadcasts/active");
        if (mounted) setBcs(data || []);
      } catch (err) {
        // Non-blocking: banner just stays empty. Log so production
        // outages don't fail silently.
        console.error("BroadcastBanner: failed to load /broadcasts/active", err);
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (!coords || bcs.length === 0) return null;

  const visible = bcs.filter((b) => {
    if (dismissed.has(b.id)) return false;
    const d = distanceKm(coords.latitude, coords.longitude, b.latitude, b.longitude);
    return d <= b.radius_km;
  }).sort((a, b) => {
    const rankDiff = (SEV_RANK[b.severity] || 0) - (SEV_RANK[a.severity] || 0);
    if (rankDiff !== 0) return rankDiff;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  if (visible.length === 0) return null;

  const dismiss = (id) => {
    const next = new Set(dismissed); next.add(id); setDismissed(next); writeDismissed(next);
  };

  const open = visible.find((b) => b.id === openId) || null;

  return (
    <>
      <div className="border-b-2" data-testid="broadcast-banner">
        {visible.map((b) => {
          const hasPhoto = Array.isArray(b.attachments) && b.attachments.some((a) => a.kind === "image");
          const heroImg = hasPhoto ? b.attachments.find((a) => a.kind === "image") : null;
          return (
            <div
              key={b.id}
              className={`px-4 py-3 flex items-start gap-3 border-b-2 last:border-b-0 ${colors[b.severity] || colors.warning}`}
              data-testid={`broadcast-${b.id}`}
            >
              <Megaphone className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={2.5} />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] opacity-90">
                  Diffusion officielle · {b.severity === "critical" ? "CRITIQUE" : b.severity === "warning" ? "AVERTISSEMENT" : "INFO"}
                </div>
                <div className="font-display text-base font-black tracking-tight mt-0.5">{b.title}</div>
                <p className="text-[12px] leading-relaxed mt-1 opacity-95 line-clamp-3">{b.message}</p>
                {hasPhoto && (
                  <button
                    type="button"
                    onClick={() => setOpenId(b.id)}
                    data-testid={`broadcast-open-${b.id}`}
                    className="mt-2 flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur px-2 py-1.5 transition-colors"
                  >
                    <img src={mediaUrl(heroImg.url)} alt="" className="w-10 h-10 object-cover border border-white/40" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
                      <Eye className="w-3 h-3 inline mr-1" /> Voir photo · {b.attachments.length} pièce{b.attachments.length > 1 ? "s" : ""}
                    </span>
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(b.id)}
                data-testid={`broadcast-dismiss-${b.id}`}
                aria-label="Fermer"
                className="p-1 hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Full-screen broadcast viewer */}
      {open && (
        <BroadcastViewer
          broadcast={open}
          onClose={() => setOpenId(null)}
          onReportSighting={() => { setReportFor(open); setOpenId(null); }}
        />
      )}

      {/* Sighting report modal */}
      {reportFor && (
        <SightingReportModal
          broadcast={reportFor}
          coords={coords}
          onClose={() => setReportFor(null)}
          onSubmitted={() => { setReportFor(null); toast.success("Merci ! Votre signalement a été transmis aux autorités."); }}
        />
      )}
    </>
  );
};

// ---------------------- Viewer (full-screen carousel) ----------------------
const BroadcastViewer = ({ broadcast, onClose, onReportSighting }) => {
  const [idx, setIdx] = useState(0);
  const atts = broadcast.attachments || [];
  const cur = atts[idx];

  const prev = () => setIdx((i) => (i - 1 + atts.length) % atts.length);
  const next = () => setIdx((i) => (i + 1) % atts.length);

  return (
    <div
      className="fixed inset-0 z-[600] bg-black/95 backdrop-blur-sm flex flex-col"
      data-testid={`broadcast-viewer-${broadcast.id}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 text-white border-b border-white/10">
        <div className="min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-amber-300">
            Diffusion · {broadcast.severity === "critical" ? "CRITIQUE" : broadcast.severity === "warning" ? "AVERTISSEMENT" : "INFO"}
          </div>
          <div className="font-display text-base font-black truncate">{broadcast.title}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid={`viewer-close-${broadcast.id}`}
          aria-label="Fermer"
          className="p-2 hover:bg-white/10 text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Image / media */}
      <div className="flex-1 flex items-center justify-center relative px-4 py-3">
        {cur?.kind === "image" && (
          <img src={mediaUrl(cur.url)} alt="" className="max-w-full max-h-full object-contain" data-testid="viewer-image" />
        )}
        {cur?.kind === "video" && (
          <video src={mediaUrl(cur.url)} className="max-w-full max-h-full" controls autoPlay data-testid="viewer-video" />
        )}
        {cur?.kind === "audio" && (
          <div className="w-full max-w-md text-white space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">Enregistrement audio</div>
            <audio src={mediaUrl(cur.url)} controls className="w-full" data-testid="viewer-audio" />
          </div>
        )}
        {atts.length > 1 && (
          <>
            <button type="button" onClick={prev} aria-label="Précédent" className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 text-white">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button type="button" onClick={next} aria-label="Suivant" className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 text-white">
              <ChevronRight className="w-6 h-6" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-[10px] font-mono px-2 py-1">
              {idx + 1} / {atts.length}
            </div>
          </>
        )}
      </div>

      {/* Message text */}
      {broadcast.message && (
        <div className="px-4 py-3 text-white/90 text-sm leading-relaxed border-t border-white/10 max-h-[20vh] overflow-y-auto">
          {broadcast.message}
        </div>
      )}

      {/* Sighting CTA */}
      <button
        type="button"
        onClick={onReportSighting}
        data-testid={`viewer-report-${broadcast.id}`}
        className="m-3 bg-emerald-500 hover:bg-emerald-400 active:scale-[0.99] text-black py-3 text-sm font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-all"
      >
        <Eye className="w-4 h-4" strokeWidth={3} /> J&apos;ai vu cette personne / scène
      </button>
    </div>
  );
};

// ---------------------- Sighting Report Modal ----------------------
const SightingReportModal = ({ broadcast, coords, onClose, onSubmitted }) => {
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [shareLocation, setShareLocation] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const phone = userIdentity.getPhone() || null;
      const payload = {
        broadcast_id: broadcast.id,
        description,
        attachments,
        reporter_phone: phone,
      };
      if (shareLocation && coords) {
        payload.coordinates = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
        };
      }
      await api.post(`/broadcasts/${broadcast.id}/sightings`, payload);
      onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi");
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[650] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2"
      data-testid={`sighting-modal-${broadcast.id}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border-2 border-rescue-ink"
      >
        <div className="bg-emerald-500 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-black">
            <Eye className="w-4 h-4" strokeWidth={3} />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Signalement de témoignage</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer" data-testid={`sighting-close-${broadcast.id}`} className="text-black p-1 hover:bg-black/10">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rescue-mute">
            Diffusion concernée
          </div>
          <div className="text-sm font-display font-black -mt-2">{broadcast.title}</div>

          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rescue-mute">
              Ce que vous avez vu
            </span>
            <textarea
              data-testid={`sighting-description-${broadcast.id}`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Quand ? Où exactement ? Direction prise ? Détails utiles…"
              className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-emerald-500 focus:outline-none px-3 py-2 text-sm resize-none"
            />
          </label>

          <AttachmentPicker
            attachments={attachments}
            onChange={setAttachments}
            testidPrefix={`sighting-attach-${broadcast.id}`}
            max={3}
          />

          {coords && (
            <label className="flex items-center gap-2 cursor-pointer p-2 border-2 border-rescue-line">
              <input
                type="checkbox"
                checked={shareLocation}
                onChange={(e) => setShareLocation(e.target.checked)}
                data-testid={`sighting-share-location-${broadcast.id}`}
                className="w-4 h-4 accent-emerald-600"
              />
              <MapPin className="w-3.5 h-3.5 text-emerald-700" />
              <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-rescue-ink">
                Partager ma position au moment du témoignage
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid={`sighting-submit-${broadcast.id}`}
            className="w-full bg-rescue-ink hover:bg-rescue-red text-white py-2.5 text-xs font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? "Envoi…" : "Envoyer le signalement"}
          </button>
        </div>
      </form>
    </div>
  );
};
